import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import VariationSelector from "../../common/VariationSelector";
import ProductInformation from "./ProductInformation";
import ProductMeta from "./ProductMeta";
import type {
  TransformedProduct,
  TransformedVariation,
} from "../../../types/productApiTypes";
import type { SelectedAttributeSlugs } from "../../../hooks/useProductVariationSelector";

interface ProductDetailsProps {
  product: TransformedProduct;
  variations?: TransformedVariation[];
  variationsLoading?: boolean;
  selectedVariation: TransformedVariation | null;
  selectedAttributes: SelectedAttributeSlugs;
  onAttributeSelect: (attributeName: string, valueSlug: string) => void;
}

const ProductDetails: React.FC<ProductDetailsProps> = ({
  product,
  variationsLoading = false,
  selectedVariation = null,
  selectedAttributes = {},
  onAttributeSelect,
}) => {
  const { t } = useTranslation("common");
  const {
    name: productName,
    shortDescription,
    enrichedCategories = [],
    enrichedTags = [],
    enrichedBrands = [],
    enrichedAttributes = [],
    type: productType,
    sku: productSku,
  } = product;

  const isVariable = productType === "variable";
  const shopUrl = window.themeVariables?.shopUrl || "/shop/";
  const isLoading = variationsLoading;

  const hasRequiredAttributes = useMemo(() => {
    if (!isVariable) return true;
    return enrichedAttributes
      .filter((attr) => attr.hasVariations && attr.taxonomy)
      .every((attr) => !!selectedAttributes[attr.taxonomy!]);
  }, [isVariable, enrichedAttributes, selectedAttributes]);

  const isCombinationAvailable = !!selectedVariation;

  const getSku = (): string => {
    if (isVariable && isCombinationAvailable && selectedVariation?.sku) {
      return selectedVariation.sku;
    }
    return productSku;
  };
  const currentSku = getSku();

  return (
    <div className="product-details">
      <ProductInformation
        productName={productName}
        shortDescription={shortDescription}
        variationsLoading={isLoading}
        isVariable={isVariable}
        isCombinationAvailable={isCombinationAvailable}
      />

      {isVariable && (
        <VariationSelector
          enrichedAttributes={enrichedAttributes}
          selectedAttributes={selectedAttributes}
          onAttributeSelect={onAttributeSelect}
          variationsLoading={isLoading}
          containerClassName="variations"
          rowClassName="variation-row"
        />
      )}

      {isVariable && !isLoading && !hasRequiredAttributes && (
        <div className="variation-notice">
          {t("product.selectOptionsNotice")}
        </div>
      )}
      {isVariable &&
        hasRequiredAttributes &&
        !isLoading &&
        !isCombinationAvailable && (
          <div className="variation-required-notice">
            {t("product.unavailableCombinationNotice")}
          </div>
        )}

      <ProductMeta
        sku={currentSku}
        categories={enrichedCategories}
        tags={enrichedTags}
        brands={enrichedBrands}
        shopUrl={shopUrl}
      />
    </div>
  );
};

export default ProductDetails;
